import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  getPortalToken,
  portalCompanies,
  portalLogout,
} from "@/modules/tramites/materialidad/clientPortalApi";
import PortalMaterialidadShell from "./PortalMaterialidadShell";

export default function PortalMaterialidadCompaniesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!getPortalToken()) {
      navigate("/portal-materialidad/login", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await portalCompanies();
        if (cancelled) return;
        setItems(data?.items || []);
        setUser(data?.user || null);
        if (data?.user?.must_change_password) {
          navigate("/portal-materialidad/cambiar-password", { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.detail || "Sesión expirada.");
          navigate("/portal-materialidad/login", { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const logout = async () => {
    await portalLogout();
    navigate("/portal-materialidad", { replace: true });
  };

  return (
    <PortalMaterialidadShell>
      <div className="space-y-5" data-testid="portal-materialidad-companies">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-[#0F2D5C]">Mis empresas</h1>
            {user && (
              <p className="text-sm text-slate-600 mt-1">
                {user.name} · {user.login_code || user.email}
              </p>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={logout} data-testid="portal-logout">
            <LogOut className="w-4 h-4 mr-1" /> Salir
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando empresas…
          </p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">No hay empresas asignadas a su cuenta.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((c) => (
              <li key={c.client_id}>
                <Link
                  to={`/portal-materialidad/empresas/${encodeURIComponent(c.client_id)}`}
                  className="block rounded-sm border border-slate-200 bg-white px-4 py-3 no-underline hover:border-[#0F2D5C]/40"
                  data-testid={`portal-company-${c.client_id}`}
                >
                  <p className="text-sm font-semibold text-slate-900">{c.client_name}</p>
                  <p className="text-xs text-slate-500">
                    RFC {c.client_rfc || "—"} · solicitudes abiertas {c.pending_requests || 0}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PortalMaterialidadShell>
  );
}
